import { useEffect, useState } from "react";
import type { Facets } from "../api/types";
import { activeCount, emptyFilters, type Filters, type Tab } from "../filters";
import { MultiSelect } from "./MultiSelect";
import { DateFilter } from "./DateFilter";
import { useRights } from "../rights";

type Props = {
  tab: Tab;
  filters: Filters;
  facets: Facets | null;
  total: number;
  loading: boolean;
  onChange: (patch: Partial<Filters>) => void;
  onReset: () => void;
};

/**
 * One filter bar per tab, and every option list is built from what that tab
 * actually holds -- so a filter can never come up empty.
 */
export function FilterBar({
  tab, filters: f, facets, total, loading, onChange, onReset,
}: Props) {
  // The text box is typed into, so it keeps its own state and settles before
  // asking the server. Every other control changes once per click.
  const [text, setText] = useState(f.q);
  useEffect(() => setText(f.q), [f.q]);
  useEffect(() => {
    if (text === f.q) return;
    const t = setTimeout(() => onChange({ q: text }), 220);
    return () => clearTimeout(t);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  // A month the selected year has none of is dropped rather than left showing
  // an empty page.
  useEffect(() => {
    if (!f.ranging && f.month && facets?.months) {
      const hit = facets.months.find((m) => m.value === f.month);
      if (hit && hit.count === 0) onChange({ month: "" });
    }
  }, [facets, f.month, f.ranging]); // eslint-disable-line react-hooks/exhaustive-deps

  const shops = facets?.shops ?? [];
  const solo = facets?.solo?.[0];
  const active = activeCount(f);
  const rights = useRights();

  // On a phone the bar folds: fifteen controls stacked would fill the screen,
  // and the bar is sticky. The search box, the count and a "Filters" button
  // stay out; everything else shows when asked. A desktop never folds it.
  const [open, setOpen] = useState(() => !window.matchMedia("(max-width: 640px)").matches);

  return (
    <div className={`controls${open ? "" : " closed"}`}>
      <button type="button" className="filtertoggle keep" aria-expanded={open}
              onClick={() => setOpen((v) => !v)}>
        {open ? "Hide filters" : `Filters${active > 0 ? ` (${active})` : ""}`}
      </button>
      <input
        type="search"
        className="keep"
        placeholder="Search artist, title, label, tag, date…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        title="several keywords all have to match: dub reggae finds the records that are both"
      />

      <select value={f.shop} onChange={(e) => onChange({ shop: e.target.value })}>
        <option value="">Any shop</option>
        {shops.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label} ({s.count})
          </option>
        ))}
      </select>

      {/* No Bandcamp tags on the unmatched tab -- there are none to filter by. */}
      {tab === "matched" && (
        <MultiSelect
          kind="tags"
          noneLabel="Any Bandcamp tag"
          findLabel="find a tag…"
          options={facets?.tags ?? []}
          selected={f.tags}
          onChange={(tags) => onChange({ tags })}
        />
      )}

      <MultiSelect
        kind="styles"
        noneLabel="Any Discogs style"
        findLabel="find a style…"
        options={facets?.styles ?? []}
        selected={f.styles}
        onChange={(styles) => onChange({ styles })}
      />

      <MultiSelect
        kind="genres"
        noneLabel="Any shop genre"
        findLabel="find a genre…"
        options={facets?.genres ?? []}
        selected={f.genres}
        onChange={(genres) => onChange({ genres })}
      />

      <MultiSelect
        kind="labels"
        noneLabel="Any label"
        findLabel="find a label…"
        options={facets?.labels ?? []}
        selected={f.labels}
        onChange={(labels) => onChange({ labels })}
      />

      <MultiSelect
        kind="meters"
        noneLabel="Any meter"
        findLabel="find a meter…"
        options={facets?.meters ?? []}
        selected={f.meters}
        onChange={(meters) => onChange({ meters })}
      />

      <MultiSelect
        kind="keys"
        noneLabel="Any key"
        findLabel="find a key…"
        options={facets?.keys ?? []}
        selected={f.keys}
        onChange={(keys) => onChange({ keys })}
      />

      <DateFilter
        filters={f}
        years={facets?.years ?? []}
        months={facets?.months ?? []}
        onChange={onChange}
      />

      {/* Bands first, then the two verdicts -- "no steady pulse" and "not
          measured yet" -- because a tempo is a state, not a number, and the
          leftovers of a sweep are worth finding as much as a 120. Shown on
          both tabs now that a record off Bandcamp has a shop's clips as its
          tracks. */}
      <select
        value={f.bpm}
        onChange={(e) => onChange({ bpm: e.target.value })}
        title="releases with a track in a 10-BPM band, or with a track whose tempo is unknown or not measured yet"
      >
        <option value="">Any BPM</option>
        {(facets?.bpm ?? []).filter((b) => /^\d/.test(b.value)).map((b) => (
          <option key={b.value} value={b.value}>
            {b.label} ({b.count})
          </option>
        ))}
        {(facets?.bpm ?? []).some((b) => !/^\d/.test(b.value)) && <option disabled>──</option>}
        {(facets?.bpm ?? []).filter((b) => !/^\d/.test(b.value)).map((b) => (
          <option key={b.value} value={b.value}>
            {b.label} ({b.count})
          </option>
        ))}
      </select>

      {/* Shown only when the tab holds a mix of exclusive and shared records:
          offering it where every record is exclusive filters nothing. */}
      {solo && solo.count > 0 && solo.count < total && (
        <label className={`chk${f.solo ? " on" : ""}`}>
          <input
            type="checkbox"
            checked={f.solo}
            onChange={(e) => onChange({ solo: e.target.checked })}
          />
          Only in one shop ({solo.count})
        </label>
      )}

      {/* Mine, and nobody else's: the API binds this to the token, so the
          URL carries only the fact of the filter, never whose it is. */}
      {rights.has("favorites") && (
        <label className={`chk fav${f.fav ? " on" : ""}`} title="only the releases I marked">
          <input
            type="checkbox"
            checked={f.fav}
            onChange={(e) => onChange({ fav: e.target.checked })}
          />
          ♥ Favorites
        </label>
      )}

      {active > 0 && (
        <button type="button" className="reset keep" onClick={onReset}>
          Reset ({active})
        </button>
      )}

      <span className="count keep">
        {loading && <span className="spinner" />}
        {total.toLocaleString()} release{total === 1 ? "" : "s"}
      </span>
    </div>
  );
}

export { emptyFilters };
